import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { TrailTypeEnum } from '@modules/stocks-module/orders/enums/trail-type.enum'
import { TranslatePipe } from '@ngx-translate/core'
import { TuiDataListComponent, TuiDropdown, TuiIcon, TuiNumberFormat, TuiTextfield } from '@taiga-ui/core'
import { TuiInputNumber } from '@taiga-ui/kit'
import { Subject, takeUntil } from 'rxjs'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { ImageComponent } from '@shared/components/common/image/image.component'
import { TrailInterface } from '@modules/stocks-module/orders/interfaces/trail.interface'
import { FractionDivisorEnum } from '@modules/stocks-module/orders/enums/fraction-divisor.enum'
import { MinTrailPrice } from '@modules/stocks-module/orders/constants/min-trail-price'
import { InputNumberSignsDirective } from '@shared/directives/input-number-signs.directive'

@UseLocales({ ru, en })
@Component({
	selector: 'app-trailing-stop-controls',
	templateUrl: './trailing-stop-controls.component.html',
	styleUrl: './trailing-stop-controls.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		ReactiveFormsModule,
		TranslatePipe,
		TuiTextfield,
		TuiInputNumber,
		TuiNumberFormat,
		TuiDropdown,
		TuiDataListComponent,
		TuiIcon,
		ImageComponent,
		InputNumberSignsDirective,
	],
})
export class TrailingStopControlsComponent implements OnInit, OnChanges, OnDestroy {
	protected readonly math: typeof Math = Math
	protected readonly trailTypeEnum: typeof TrailTypeEnum = TrailTypeEnum
	protected readonly minTrailPrice: number = MinTrailPrice
	protected readonly fractionDivisorEnum: typeof FractionDivisorEnum = FractionDivisorEnum

	public tickerPrice: InputSignal<number> = input<number>(0)
	public disabled: InputSignal<boolean> = input<boolean>(false)

	public changeTrail: OutputEmitterRef<TrailInterface> = output<TrailInterface>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _formBuilder: FormBuilder = inject<FormBuilder>(FormBuilder)

	protected form: FormGroup | null = null
	protected isOpenTrailTypes: WritableSignal<boolean> = signal<boolean>(false)
	protected activeTrailTypeLabel: WritableSignal<string> = signal<string>('trailingStop.percent')
	protected minValue: WritableSignal<number> = signal<number>(0.1)
	protected precision: WritableSignal<number> = signal<number>(2)
	private _getDefaultValue(type: TrailTypeEnum): number {
		if (type === this.trailTypeEnum.PERCENT) return 1
		const price: number = this.tickerPrice()
		if (price <= 0) return this.minTrailPrice

		const onePercent: number = price * this.minTrailPrice
		return onePercent >= this.minTrailPrice
			? this.math.round(onePercent * this.fractionDivisorEnum.HUNDREDTHS) / this.fractionDivisorEnum.HUNDREDTHS
			: this.math.round(onePercent * this.fractionDivisorEnum.TEN_THOUSANDTHS) /
					this.fractionDivisorEnum.TEN_THOUSANDTHS
	}

	private _setPrecision(type: TrailTypeEnum): void {
		if (type === this.trailTypeEnum.PERCENT) {
			this.precision.set(2)
			return
		}

		const price: number = this.tickerPrice()
		this.precision.set(price > 0 && price * this.minTrailPrice < this.minTrailPrice ? 4 : 2)
	}

	private _initForm(): void {
		this.form = this._formBuilder.group({
			type: [this.trailTypeEnum.PERCENT],
			value: [
				this._getDefaultValue(this.trailTypeEnum.PERCENT),
				[Validators.required, Validators.min(this.minTrailPrice)],
			],
		})
		if (this.disabled()) this.form.disable()
	}

	private _listenFormControlsChanges(): void {
		this.form
			?.get('value')
			?.valueChanges.pipe(takeUntil(this._destroy$))
			.subscribe((): void => {
				if (this.disabled()) return
				this._emitTrail()
			})

		this._emitTrail()
	}

	private _emitTrail(): void {
		const type: TrailTypeEnum = this.form?.get('type')?.value
		const value: number = +(this.form?.get('value')?.value || 0)
		const positiveValue: number | null = value > 0 ? value : null

		this.changeTrail.emit({
			trail_price: type === this.trailTypeEnum.PRICE ? positiveValue : null,
			trail_percent: type === this.trailTypeEnum.PERCENT ? positiveValue : null,
		})
	}

	protected setType(type: TrailTypeEnum): void {
		if (!this.form) return
		if (this.form.get('type')?.value === type) return

		this.form.get('type')?.setValue(type)
		this.form.get('value')?.setValue(this._getDefaultValue(type))
		this.activeTrailTypeLabel.set(type === this.trailTypeEnum.PRICE ? 'trailingStop.price' : 'trailingStop.percent')
		this.minValue.set(type === this.trailTypeEnum.PERCENT ? 0.1 : this.minTrailPrice)
		this._setPrecision(type)
		this.isOpenTrailTypes.set(false)
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['disabled']) {
			this.disabled() ? this.form?.disable() : this.form?.enable()
		}
	}

	ngOnInit(): void {
		this._initForm()
		this._listenFormControlsChanges()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
